import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PhotoUploadModal from './components/PhotoUploadModal'; // Fixed path for your structure

// 🔧 CRITICAL: Ensure base URL is set
if (!axios.defaults.baseURL) {
    axios.defaults.baseURL = '/';
    console.log('🔧 Set axios baseURL to:', axios.defaults.baseURL);
}

const CONFIRM_LOCK_FLAG = "VISIT_CONFIRMED_LOCKED";

function VisitHistoryModal({ 
    isOpen, 
    onClose, 
    patientContext, 
    onNewVisit,
    onLoadVisit 
}) {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groupedVisits, setGroupedVisits] = useState({});
    const [error, setError] = useState(null);
    
    // Photo upload modal state
    const [showPhotoUpload, setShowPhotoUpload] = useState(false);
    
    // Delete confirmation modal state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [visitToDelete, setVisitToDelete] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch visits when modal opens
    useEffect(() => {
        if (isOpen && patientContext.branchHN) {
            console.log('🏥 Modal opened for patient:', patientContext.branchHN);
            fetchVisits();
        }
    }, [isOpen, patientContext.branchHN]);

    // 🛡️ THE IPAD TAB ARMOR: Detect background tab returning to foreground
    useEffect(() => {
        const handleVisibilityChange = () => {
            // If the tab becomes active again AND the modal is currently open
            if (document.visibilityState === 'visible' && isOpen && patientContext.branchHN) {
                console.log('🔄 Tab returned to foreground! Forcing fresh fetch to clear stale cache...');
                // Optional: You can add an alert here if you want to scold the staff, haha!
                // alert('⚠️ Refreshing patient data to prevent accidental overwrites.');
                fetchVisits(); 
            }
        };

        // Attach the listener to the browser tab
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup the listener when the modal closes or unmounts
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isOpen, patientContext.branchHN]);

    const fetchVisits = async () => {
        setLoading(true);
        setError(null);
        
        const patientId = patientContext.branchHN;
        
        try {
            // Verify patient exists and fetch visits
            await axios.get(`/api/patient/${patientId}/info`);
            const response = await axios.get(`/api/patient/${patientId}/visits`);
            
            if (response.data.success) {
                const rawVisits = response.data.visits || [];
                
                if (rawVisits.length === 0) {
                    setVisits([]);
                    setGroupedVisits({});
                } else {
                    const allVisits = [];
                    
                    // สเต็ปที่ 1: แตกแถวข้อมูล (Flatten) เอาใบงานทั้งหมดออกมากองรวมกันก่อน ไม่สนใจกลุ่มเก่าที่หลังบ้านมัดมา
                    rawVisits.forEach(dateGroup => {
                        if (dateGroup.visits && Array.isArray(dateGroup.visits)) {
                            dateGroup.visits.forEach(visit => {
                                const isPdf = visit.image_path && visit.image_path.toLowerCase().endsWith('.pdf');

                                const processedVisit = {
                                    ...visit,
                                    isLocked: visit.notes && visit.notes.includes(CONFIRM_LOCK_FLAG),
                                    isPhoto: visit.visit_type === 'photo',
                                    isPdf: isPdf,
                                    isDigital: visit.visit_type === 'digital' || !visit.visit_type
                                };
                                
                                allVisits.push(processedVisit);
                            });
                        }
                    });
                    
                    // สเต็ปที่ 2: 🚀 RE-GROUPING เทพ! สั่งจัดกลุ่มใหม่ตามนัดหมายรักษาจริง
                    const cleanGrouped = {};
                    allVisits.forEach(visit => {
                        // ดึงตามวันนัดหมายรักษา ถ้าไม่มีค่อยถอยไปใช้วันสร้างไฟล์ (visit_date)
                        const targetDate = visit.appointment_date || visit.visit_date;
                        
                        if (targetDate) {
                            if (!cleanGrouped[targetDate]) {
                                cleanGrouped[targetDate] = [];
                            }
                            cleanGrouped[targetDate].push(visit);
                        }
                    });
                    
                    setVisits(allVisits);
                    setGroupedVisits(cleanGrouped); // ส่งกลุ่มที่ชะล้างมลทินแล้วไปให้ UI แสดงผล
                }
            } else {
                setError('API returned unsuccessful response');
                setVisits([]);
            }
        } catch (error) {
            console.error('❌ Error fetching visits:', error);
            setError('Failed to load visits');
            setVisits([]);
        } finally {
            setLoading(false);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            
            if (date.toDateString() === today.toDateString()) return '📅 Today';
            if (date.toDateString() === yesterday.toDateString()) return '📅 Yesterday';
            
            return date.toLocaleDateString('en-GB', { 
                day: 'numeric', month: 'long', year: 'numeric' 
            });
        } catch (error) {
            return dateString;
        }
    };

    // Format time for display
    const formatTime = (timeString) => {
        try {
            const time = new Date(timeString);
            return time.toLocaleTimeString('en-GB', { 
                hour: '2-digit', minute: '2-digit', hour12: false
            });
        } catch (error) {
            return timeString;
        }
    };

    const handleLoadVisit = (visit) => {
        console.log('📖 Loading visit via React state:', visit.id);
        if (onLoadVisit) {
            onLoadVisit({
                date: visit.visit_date,
                template: visit.template_name,
                visitData: visit
            });
        }
        onClose();
    };

    const handleNewVisit = () => {
        if (onNewVisit) onNewVisit();
        onClose();
    };

    const handlePhotoUploadSuccess = () => {
        setShowPhotoUpload(false);
        fetchVisits();
    };

    // DELETE LOGIC
    const handleDeleteClick = (visit) => {
        setVisitToDelete(visit);
        setDeleteReason('');
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteReason.trim() || !visitToDelete) return;

        setIsDeleting(true);
        try {
            const response = await axios.post(
                `/api/patient/${patientContext.branchHN}/visit/${visitToDelete.id}/soft-delete`,
                {
                    reason: deleteReason.trim(),
                    deletedBy: patientContext.doctor || 'Staff'
                }
            );

            if (response.data.success) {
                setShowDeleteConfirm(false);
                fetchVisits();
            }
        } catch (error) {
            alert('Delete failed: ' + error.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setVisitToDelete(null);
        setDeleteReason('');
    };

    const getVisitTypeIcon = (visit) => {
        if (visit.isPdf) return '📄';
        if (visit.isPhoto) return '📸';
        return '✏️';
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '12px',
                maxWidth: '800px', maxHeight: '90vh', width: '90%',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
            }}>
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#333' }}>📋 Visit History</h2>
                    <div style={{ fontSize: '16px', color: '#666' }}>Patient: {patientContext.name}</div>
                    
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                        <button onClick={handleNewVisit} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>➕ New Visit/เปิด OPD ใหม่</button>
                        <button onClick={() => setShowPhotoUpload(true)} style={{ padding: '10px 20px', backgroundColor: '#FF9800', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>📸 อัพโหลด รูป/PDF</button>
                        <button onClick={() => window.open(`/before-after.html?patient=${patientContext.branchHN}`, '_blank')} 
                                style={{ padding: '10px 20px', backgroundColor: '#E91E63', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                🎞️ อัลบั้ม Before - After
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                    {loading && <div style={{ textAlign: 'center', padding: '40px' }}>⏳ Loading Visits...</div>}
                    {!loading && Object.keys(groupedVisits).sort((a, b) => new Date(b) - new Date(a)).map(date => (
                        <div key={date} style={{ marginBottom: '25px' }}>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', borderBottom: '2px solid #e0e0e0', marginBottom: '12px' }}>{formatDate(date)}</div>
                            {groupedVisits[date].sort((a, b) => new Date(b.visit_time) - new Date(a.visit_time))
                                    .filter(visit => !visit.template_name.includes('Slot'))
                                    .map(visit => {
                                const isLocked = visit.isLocked;
                                let bgColor = visit.isPdf ? '#e3f2fd' : (visit.isPhoto ? '#fff3e0' : '#e8f5e9');
                                let borderColor = visit.isPdf ? '#2196F3' : (visit.isPhoto ? '#FF9800' : '#4CAF50');

                                return (
                                    <div key={visit.id} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '15px', marginBottom: '10px',
                                        backgroundColor: isLocked ? '#f5f5f5' : bgColor,
                                        borderRadius: '8px', border: `2px solid ${isLocked ? '#e0e0e0' : borderColor}`
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 'bold' }}>{getVisitTypeIcon(visit)} {formatTime(visit.visit_time)} - {visit.template_name}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>{visit.doctor_name && `👨‍⚕️ ${visit.doctor_name}`}</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={() => handleLoadVisit(visit)} style={{ padding: '8px 16px', backgroundColor: isLocked ? '#9E9E9E' : borderColor, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                                {isLocked ? '🔒 View' : 'View/Edit'}
                                            </button>
                                            {!isLocked && (
                                                <button onClick={() => handleDeleteClick(visit)} style={{ padding: '8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>🗑️</button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{ padding: '20px', borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
                    <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>✖️ Close</button>
                </div>
            </div>

            {/* Photo Upload Modal */}
            {showPhotoUpload && (
                <PhotoUploadModal isOpen={showPhotoUpload} onClose={() => setShowPhotoUpload(false)} patientContext={patientContext} onUploadSuccess={handlePhotoUploadSuccess} />
            )}

            {/* Delete Confirm Modal */}
            {showDeleteConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001 }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', maxWidth: '450px', width: '90%' }}>
                        <h3 style={{ color: '#dc3545' }}>⚠️ Confirm Delete</h3>
                        <p>Delete visit for {visitToDelete?.visit_date}?</p>
                        <select value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '20px' }}>
                            <option value="">-- Select Reason --</option>
                            <option value="Duplicate">Duplicate</option>
                            <option value="Error">Error</option>
                            <option value="Patient Requested">Patient Requested</option>
                        </select>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={handleCancelDelete} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px' }}>Cancel</button>
                            <button onClick={handleConfirmDelete} disabled={!deleteReason || isDeleting} style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '6px' }}>
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default VisitHistoryModal;